import React, { useState, useEffect } from 'react';
import axios from 'axios';
import UserLayout from '../../components/user/UserLayout';
import { BarChart3, TrendingUp, Users } from 'lucide-react';

const TYPE_LABELS = {
  referral_commission: { label: 'Referral Commission', color: 'text-primary-400', bg: 'bg-primary-500/20' },
  profit_commission:   { label: 'Profit Share',        color: 'text-green-400',   bg: 'bg-green-500/20' },
  achievement_bonus:   { label: 'Achievement Bonus',   color: 'text-pink-400',    bg: 'bg-pink-500/20' },
  setup_fee:          { label: 'Setup Fee',            color: 'text-amber-400',  bg: 'bg-amber-500/20' },
  wallet_topup:       { label: 'Wallet Topup',         color: 'text-blue-400',   bg: 'bg-blue-500/20' },
  profit_deduction:   { label: 'Profit Deduction',     color: 'text-red-400',    bg: 'bg-red-500/20' },
  withdrawal:         { label: 'Withdrawal',           color: 'text-purple-400', bg: 'bg-purple-500/20' },
  wallet_transfer:    { label: 'Wallet Transfer',      color: 'text-blue-300',   bg: 'bg-blue-400/20' },
  admin_credit:       { label: 'Admin Credit',         color: 'text-green-300',  bg: 'bg-green-400/20' },
};

export default function CommissionPage() {
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  // Change 2: date filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [stats, setStats] = useState({ referral: 0, profit: 0 });

  useEffect(() => {
    const params = new URLSearchParams({ page, limit: 20 });
    if (filter) params.append('type', filter);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    axios.get(`/api/commission/history?${params}`).then(r => {
      setTransactions(r.data.transactions);
      setTotal(r.data.total);
    });
  }, [page, filter, startDate, endDate]);

  useEffect(() => {
    Promise.all([
      axios.get('/api/commission/history?type=referral_commission&limit=1000'),
      axios.get('/api/commission/history?type=profit_commission&limit=1000')
    ]).then(([r1, r2]) => {
      setStats({
        referral: r1.data.transactions.reduce((s, t) => s + t.amount, 0),
        profit: r2.data.transactions.reduce((s, t) => s + t.amount, 0)
      });
    });
  }, []);

  return (
    <UserLayout>
      <div className="animate-fade-in">
        <h1 className="text-2xl font-bold text-white mb-6">Transaction History</h1>

        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-5">
            <Users className="w-6 h-6 text-primary-400 mb-2" />
            <p className="text-2xl font-bold text-white">${stats.referral.toFixed(2)}</p>
            <p className="text-gray-400 text-sm">Commissions</p>
          </div>
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-5">
            <TrendingUp className="w-6 h-6 text-green-400 mb-2" />
            <p className="text-2xl font-bold text-white">${stats.profit.toFixed(2)}</p>
            <p className="text-gray-400 text-sm">Profit Share Earnings</p>
          </div>
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-5">
            <BarChart3 className="w-6 h-6 text-amber-400 mb-2" />
            <p className="text-2xl font-bold text-white">${(stats.referral + stats.profit).toFixed(2)}</p>
            <p className="text-gray-400 text-sm">Total Earned</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-dark-700 border border-white/5 rounded-2xl p-4 mb-4">
          <div className="flex flex-wrap gap-3 items-end">
            {/* Type filter */}
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Type</label>
              <select value={filter} onChange={e => { setFilter(e.target.value); setPage(1); }}
                className="bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary-500">
                <option value="">All Types</option>
                <option value="referral_commission">Referral Commission</option>
                <option value="profit_commission">Profit Share</option>
                <option value="achievement_bonus">Achievement Bonus</option>
                <option value="profit_deduction">Deductions</option>
                <option value="wallet_topup">Topups</option>
                <option value="wallet_transfer">Transfers</option>
                <option value="admin_credit">Admin Credit</option>
              </select>
            </div>
            {/* Change 2: Date filters */}
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">From Date</label>
              <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1); }}
                className="bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary-500" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">To Date</label>
              <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1); }}
                className="bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary-500" />
            </div>
            <button onClick={() => { setFilter(''); setStartDate(''); setEndDate(''); setPage(1); }}
              className="px-3 py-2 bg-dark-600 border border-white/10 text-gray-400 hover:text-white rounded-xl text-sm transition-colors">
              Clear
            </button>
          </div>
        </div>

        <div className="bg-dark-700 border border-white/5 rounded-2xl overflow-hidden">
          <div className="divide-y divide-white/5">
            {transactions.length === 0 ? (
              <div className="py-12 text-center"><BarChart3 className="w-10 h-10 text-gray-600 mx-auto mb-3" /><p className="text-gray-500">No transaction history. found</p></div>
            ) : transactions.map(t => {
              const meta = TYPE_LABELS[t.type] || { label: t.type, color: 'text-gray-400', bg: 'bg-dark-600' };
              const isCredit = ['referral_commission','profit_commission','achievement_bonus','wallet_topup','admin_credit'].includes(t.type);
              return (
                <div key={t._id} className="flex items-center gap-3 px-4 py-3">
                  <div className={`w-9 h-9 rounded-xl ${meta.bg} flex items-center justify-center flex-shrink-0`}>
                    <BarChart3 className={`w-4 h-4 ${meta.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium">{meta.label}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <p className="text-gray-500 text-xs">{new Date(t.createdAt).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}</p>
                      {t.commissionLevel && <span className="text-xs text-gray-600">• Level {t.commissionLevel}</span>}
                      {t.fromUser && <span className="text-xs text-gray-600">• from {t.fromUser.name}</span>}
                      {t.commissionPercent && <span className="text-xs text-gray-600">• {t.commissionPercent}%</span>}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`font-bold text-sm ${isCredit ? 'text-primary-400' : 'text-red-400'}`}>
                      {isCredit ? '+' : '-'}${t.amount.toFixed(2)}
                    </p>
                    <p className="text-gray-500 text-xs capitalize">{t.walletType}</p>
                  </div>
                </div>
              );
            })}
          </div>
          {total > 20 && (
            <div className="p-4 border-t border-white/5 flex items-center justify-between">
              <p className="text-gray-400 text-sm">Page {page} of {Math.ceil(total/20)}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage(p=>Math.max(1,p-1))} disabled={page===1} className="px-3 py-1.5 bg-dark-600 text-white rounded-lg text-sm disabled:opacity-50">Prev</button>
                <button onClick={() => setPage(p=>p+1)} disabled={page*20>=total} className="px-3 py-1.5 bg-dark-600 text-white rounded-lg text-sm disabled:opacity-50">Next</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </UserLayout>
  );
}
